/**
 * Leviathan Web - Order Detail Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Truck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import OrderForm from '@components/orders/OrderForm';
import { API_URL } from '@/config/api';

interface OrderItem {
  id: string;
  productId: string;
  quantity: number;
  deliveredQuantity: number;
  unit: string;
  unitPrice: number;
  lineTotal: number;
  notes: string | null;
  product: {
    id: string;
    name: string;
    sku: string;
    category: string;
  };
}

interface Dispatch {
  id: string;
  dispatchNumber: string;
  status: string;
  scheduledDate: string;
  driver: { firstName: string; lastName: string } | null;
  truck: { unitNumber: string } | null;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  status: string;
  total: number;
}

interface Order {
  id: string;
  orderNumber: string;
  status: string;
  orderType: string;
  priority: string;
  customerId: string;
  customer: {
    id: string;
    name: string;
    customerNumber: string;
    email: string | null;
    phone: string | null;
  };
  orderDate: string;
  requestedDate: string | null;
  scheduledDate: string | null;
  completedDate: string | null;
  deliveryAddress: string | null;
  deliveryCity: string | null;
  deliveryState: string | null;
  deliveryZip: string | null;
  deliveryInstructions: string | null;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  deliveryFee: number;
  discount: number;
  total: number;
  paymentTerms: string | null;
  paymentStatus: string;
  poNumber: string | null;
  notes: string | null;
  internalNotes: string | null;
  items: OrderItem[];
  dispatches?: Dispatch[];
  invoice?: Invoice | null;
  createdAt: string;
  updatedAt: string;
}

const OrderDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [actionError, setActionError] = useState('');

  const fetchOrder = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/orders/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setOrder(data.order);
      } else {
        setOrder(null);
      }
    } catch (error) {
      console.error('Failed to fetch order:', error);
      setOrder(null);
    } finally {
      setIsLoading(false);
    }
  }, [id, accessToken]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleStatusChange = async (newStatus: string) => {
    setActionError('');
    try {
      const response = await fetch(`${API_URL}/api/orders/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        setShowStatusModal(false);
        fetchOrder();
      } else {
        const error = await response.json();
        setActionError(error.error || 'Failed to update status');
      }
    } catch (error) {
      setActionError('Failed to update status');
    }
  };

  const handleDelete = async () => {
    setActionError('');
    try {
      const response = await fetch(`${API_URL}/api/orders/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        navigate('/orders');
      } else {
        const error = await response.json();
        setActionError(error.error || 'Failed to delete order');
      }
    } catch (error) {
      setActionError('Failed to delete order');
    }
  };

  const getStatusStyle = (status: string): string => {
    switch (status) {
      case 'DRAFT':
        return 'text-text-tertiary bg-text-tertiary/10';
      case 'CONFIRMED':
        return 'text-steel bg-steel/10';
      case 'SCHEDULED':
        return 'text-amber bg-amber/10';
      case 'IN_PROGRESS':
        return 'text-warning-amber bg-warning-amber/10';
      case 'DELIVERED':
        return 'text-success-green bg-success-green/10';
      case 'INVOICED':
        return 'text-steel bg-steel/10';
      case 'CANCELLED':
        return 'text-error-red bg-error-red/10';
      default:
        return 'text-text-secondary bg-text-secondary/10';
    }
  };

  const getNextStatuses = (currentStatus: string): string[] => {
    const transitions: Record<string, string[]> = {
      DRAFT: ['CONFIRMED', 'CANCELLED'],
      CONFIRMED: ['SCHEDULED', 'CANCELLED'],
      SCHEDULED: ['IN_PROGRESS', 'CANCELLED'],
      IN_PROGRESS: ['DELIVERED', 'CANCELLED'],
      DELIVERED: ['INVOICED'],
      INVOICED: [],
      CANCELLED: [],
    };
    return transitions[currentStatus] || [];
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">&gt; Loading order...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-6">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-error-red via-steel to-error-red" />
          <div className="border-l-4 border-error-red pl-6">
            <h1 className="text-4xl font-display font-bold text-error-red tracking-wide uppercase">
              Order Not Found
            </h1>
            <Link
              to="/orders"
              className="text-text-secondary text-sm font-mono hover:text-amber transition-colors mt-2 inline-block"
            >
              &larr; Back to Orders
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Edit mode
  if (isEditing) {
    return (
      <div className="space-y-6">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
          <div className="border-l-4 border-amber pl-6">
            <h1
              className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
              style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
            >
              Edit Order
            </h1>
            <button
              onClick={() => setIsEditing(false)}
              className="text-text-secondary text-sm font-mono hover:text-amber transition-colors mt-2"
            >
              &larr; Cancel Editing
            </button>
          </div>
        </div>

        <OrderForm
          orderId={id}
          initialData={{
            customerId: order.customerId,
            orderType: order.orderType as 'DELIVERY' | 'PICKUP' | 'WILL_CALL' | 'EMERGENCY',
            priority: order.priority as 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT',
            requestedDate: order.requestedDate?.split('T')[0] || '',
            deliveryAddress: order.deliveryAddress || '',
            deliveryCity: order.deliveryCity || '',
            deliveryState: order.deliveryState || '',
            deliveryZip: order.deliveryZip || '',
            deliveryInstructions: order.deliveryInstructions || '',
            taxRate: order.taxRate,
            deliveryFee: order.deliveryFee,
            discount: order.discount,
            paymentTerms: order.paymentTerms || '',
            poNumber: order.poNumber || '',
            notes: order.notes || '',
            internalNotes: order.internalNotes || '',
          }}
          initialItems={order.items.map((item) => ({
            id: item.id,
            productId: item.productId,
            productName: item.product.name,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice,
            lineTotal: item.lineTotal,
            notes: item.notes || '',
          }))}
          onSuccess={() => {
            setIsEditing(false);
            fetchOrder();
          }}
        />
      </div>
    );
  }

  const nextStatuses = getNextStatuses(order.status);
  const canEdit = !['DELIVERED', 'INVOICED', 'CANCELLED'].includes(order.status);
  const canDelete = order.status === 'DRAFT';
  const canDispatch = order.status === 'CONFIRMED';

  // View mode
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {order.orderNumber}
              </h1>
              <div className="flex items-center space-x-4 mt-2">
                <Link
                  to="/orders"
                  className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
                >
                  &larr; Back to Orders
                </Link>
                <span className="text-text-tertiary">|</span>
                <span
                  className={`${getStatusStyle(order.status)} font-mono text-sm uppercase tracking-wider font-semibold px-2 py-1 rounded`}
                >
                  {order.status.replace('_', ' ')}
                </span>
                <span className="text-text-tertiary font-mono text-sm">{order.orderType}</span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {canDispatch && (
                <Link
                  to={`/dispatch/new?orderId=${order.id}`}
                  className="flex items-center space-x-2 px-6 py-3 bg-success-green/20 hover:bg-success-green/30 text-success-green font-mono uppercase text-sm tracking-wider transition-all border border-success-green/30 hover:border-success-green"
                >
                  <Truck className="w-4 h-4" />
                  <span>Send to Dispatch</span>
                </Link>
              )}
              {nextStatuses.length > 0 && (
                <button
                  onClick={() => setShowStatusModal(true)}
                  className="px-6 py-3 bg-amber/20 hover:bg-amber/30 text-amber font-mono uppercase text-sm tracking-wider transition-all border border-amber/30 hover:border-amber"
                >
                  Update Status
                </button>
              )}
              {canEdit && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-6 py-3 bg-steel/20 hover:bg-steel/30 text-steel font-mono uppercase text-sm tracking-wider transition-all border border-steel/30 hover:border-steel"
                >
                  Edit
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-6 py-3 bg-error-red/20 hover:bg-error-red/30 text-error-red font-mono uppercase text-sm tracking-wider transition-all border border-error-red/30 hover:border-error-red"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Order Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Info */}
        <InfoPanel title="Customer">
          <InfoRow label="Name" value={order.customer.name} highlight />
          <InfoRow label="Customer #" value={order.customer.customerNumber} />
          <InfoRow label="Email" value={order.customer.email} />
          <InfoRow label="Phone" value={order.customer.phone} />
        </InfoPanel>

        {/* Order Details */}
        <InfoPanel title="Order Details">
          <InfoRow label="Order Date" value={formatDate(order.orderDate)} />
          <InfoRow label="Requested Date" value={formatDate(order.requestedDate)} />
          <InfoRow label="Scheduled Date" value={formatDate(order.scheduledDate)} />
          <InfoRow label="Completed Date" value={formatDate(order.completedDate)} />
          <InfoRow label="PO Number" value={order.poNumber} />
          <InfoRow label="Priority" value={order.priority} />
        </InfoPanel>

        {/* Payment Info */}
        <InfoPanel title="Payment">
          <InfoRow label="Payment Terms" value={order.paymentTerms} />
          <InfoRow label="Payment Status" value={order.paymentStatus} />
          <InfoRow
            label="Total"
            value={`$${order.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
            highlight
          />
        </InfoPanel>
      </div>

      {/* Delivery Address */}
      {order.orderType === 'DELIVERY' && order.deliveryAddress && (
        <InfoPanel title="Delivery Address">
          <div className="text-text-primary font-mono">
            {order.deliveryAddress}
            <br />
            {order.deliveryCity}, {order.deliveryState} {order.deliveryZip}
          </div>
          {order.deliveryInstructions && (
            <div className="mt-4 pt-4 border-t border-panel-border">
              <span className="text-text-tertiary text-xs font-mono uppercase">Instructions:</span>
              <p className="text-text-secondary font-mono text-sm mt-1">
                {order.deliveryInstructions}
              </p>
            </div>
          )}
        </InfoPanel>
      )}

      {/* Order Items */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
          <div className="flex items-center space-x-2 mb-6">
            <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
            <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
              Order Items
            </h3>
          </div>

          <table className="w-full">
            <thead className="border-b border-panel-border">
              <tr>
                <th className="text-left py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">
                  Product
                </th>
                <th className="text-right py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">
                  Quantity
                </th>
                <th className="text-right py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">
                  Unit Price
                </th>
                <th className="text-right py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item) => (
                <tr key={item.id} className="border-b border-panel-border">
                  <td className="py-4">
                    <div className="text-text-primary font-mono">{item.product.name}</div>
                    <div className="text-text-tertiary font-mono text-xs">{item.product.sku}</div>
                  </td>
                  <td className="py-4 text-right text-text-primary font-mono">
                    {item.quantity} {item.unit}
                  </td>
                  <td className="py-4 text-right text-text-secondary font-mono">
                    ${item.unitPrice.toFixed(4)}
                  </td>
                  <td className="py-4 text-right text-amber font-mono font-semibold">
                    ${item.lineTotal.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-panel-border">
                <td colSpan={3} className="py-3 text-right text-text-secondary font-mono text-sm">
                  Subtotal
                </td>
                <td className="py-3 text-right text-text-primary font-mono">
                  ${order.subtotal.toFixed(2)}
                </td>
              </tr>
              <tr>
                <td colSpan={3} className="py-2 text-right text-text-secondary font-mono text-sm">
                  Tax ({order.taxRate}%)
                </td>
                <td className="py-2 text-right text-text-primary font-mono">
                  ${order.taxAmount.toFixed(2)}
                </td>
              </tr>
              <tr>
                <td colSpan={3} className="py-2 text-right text-text-secondary font-mono text-sm">
                  Delivery Fee
                </td>
                <td className="py-2 text-right text-text-primary font-mono">
                  ${order.deliveryFee.toFixed(2)}
                </td>
              </tr>
              {order.discount > 0 && (
                <tr>
                  <td colSpan={3} className="py-2 text-right text-text-secondary font-mono text-sm">
                    Discount
                  </td>
                  <td className="py-2 text-right text-error-red font-mono">
                    -${order.discount.toFixed(2)}
                  </td>
                </tr>
              )}
              <tr className="border-t border-panel-border">
                <td
                  colSpan={3}
                  className="py-4 text-right text-text-primary font-mono text-lg font-bold uppercase"
                >
                  Total
                </td>
                <td
                  className="py-4 text-right text-amber font-mono text-xl font-bold"
                  style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.4)' }}
                >
                  ${order.total.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Notes */}
      {(order.notes || order.internalNotes) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {order.notes && (
            <InfoPanel title="Customer Notes">
              <p className="text-text-primary font-mono text-sm whitespace-pre-wrap">{order.notes}</p>
            </InfoPanel>
          )}
          {order.internalNotes && (
            <InfoPanel title="Internal Notes">
              <p className="text-text-primary font-mono text-sm whitespace-pre-wrap">
                {order.internalNotes}
              </p>
            </InfoPanel>
          )}
        </div>
      )}

      {/* Related Records */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Dispatches */}
        <InfoPanel title="Dispatches">
          {order.dispatches && order.dispatches.length > 0 ? (
            <div className="space-y-3">
              {order.dispatches.map((dispatch) => (
                <Link
                  key={dispatch.id}
                  to={`/dispatch/${dispatch.id}`}
                  className="block bg-background p-3 border border-panel-border hover:border-amber/50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-text-primary font-mono font-semibold">
                        {dispatch.dispatchNumber}
                      </div>
                      <div className="text-text-tertiary font-mono text-xs">
                        {dispatch.driver
                          ? `${dispatch.driver.firstName} ${dispatch.driver.lastName}`
                          : 'Unassigned'}
                        {dispatch.truck && ` • ${dispatch.truck.unitNumber}`}
                      </div>
                    </div>
                    <span
                      className={`text-xs font-mono ${
                        dispatch.status === 'COMPLETED'
                          ? 'text-success-green'
                          : dispatch.status === 'EN_ROUTE'
                            ? 'text-amber'
                            : 'text-text-secondary'
                      }`}
                    >
                      {dispatch.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-text-tertiary font-mono text-sm text-center py-4">
              No dispatches yet
            </div>
          )}
          {order.status === 'CONFIRMED' && (
            <Link
              to={`/dispatch/new?orderId=${order.id}`}
              className="block text-center text-amber hover:text-amber/80 font-mono text-sm mt-4 pt-4 border-t border-panel-border"
            >
              + Send to Dispatch
            </Link>
          )}
        </InfoPanel>

        {/* Invoice */}
        <InfoPanel title="Invoice">
          {order.invoice ? (
            <Link
              to={`/invoices/${order.invoice.id}`}
              className="block bg-background p-3 border border-panel-border hover:border-amber/50 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-text-primary font-mono font-semibold">
                    {order.invoice.invoiceNumber}
                  </div>
                  <div className="text-amber font-mono text-sm">
                    ${order.invoice.total.toFixed(2)}
                  </div>
                </div>
                <span
                  className={`text-xs font-mono ${
                    order.invoice.status === 'PAID'
                      ? 'text-success-green'
                      : order.invoice.status === 'OVERDUE'
                        ? 'text-error-red'
                        : 'text-text-secondary'
                  }`}
                >
                  {order.invoice.status}
                </span>
              </div>
            </Link>
          ) : (
            <div className="text-text-tertiary font-mono text-sm text-center py-4">
              No invoice yet
            </div>
          )}
          {order.status === 'DELIVERED' && !order.invoice && (
            <Link
              to={`/invoices/new?orderId=${order.id}&customerId=${order.customerId}`}
              className="block text-center text-amber hover:text-amber/80 font-mono text-sm mt-4 pt-4 border-t border-panel-border"
            >
              + Create Invoice
            </Link>
          )}
        </InfoPanel>
      </div>

      {/* Timestamps */}
      <div className="text-text-tertiary text-xs font-mono flex items-center space-x-6">
        <span>Created: {formatDate(order.createdAt)}</span>
        <span>Updated: {formatDate(order.updatedAt)}</span>
      </div>

      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative stat-card max-w-md w-full">
            <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber" />
            <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber" />

            <div className="bg-panel-dark border border-amber/50 p-8 clip-path-octagon">
              <h3 className="text-2xl font-display text-amber mb-4 uppercase">Update Status</h3>
              <p className="text-text-secondary font-mono text-sm mb-6">
                Current status: <strong>{order.status.replace('_', ' ')}</strong>
              </p>

              {actionError && (
                <div className="mb-4 p-3 bg-error-red/10 border border-error-red/50">
                  <p className="text-error-red text-sm font-mono">&gt; {actionError}</p>
                </div>
              )}

              <div className="space-y-3 mb-6">
                {nextStatuses.map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusChange(status)}
                    className={`w-full py-3 font-mono uppercase text-sm tracking-wider transition-all border ${
                      status === 'CANCELLED'
                        ? 'bg-error-red/20 hover:bg-error-red/30 text-error-red border-error-red/30 hover:border-error-red'
                        : 'bg-steel/20 hover:bg-steel/30 text-steel border-steel/30 hover:border-steel'
                    }`}
                  >
                    {status.replace('_', ' ')}
                  </button>
                ))}
              </div>

              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setActionError('');
                }}
                className="w-full py-3 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative stat-card max-w-md w-full">
            <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-error-red" />
            <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-error-red" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-error-red" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-error-red" />

            <div className="bg-panel-dark border border-error-red/50 p-8 clip-path-octagon">
              <h3 className="text-2xl font-display text-error-red mb-4 uppercase">Confirm Delete</h3>
              <p className="text-text-secondary font-mono text-sm mb-6">
                Are you sure you want to delete order <strong>{order.orderNumber}</strong>? This
                action cannot be undone.
              </p>

              {actionError && (
                <div className="mb-4 p-3 bg-error-red/10 border border-error-red/50">
                  <p className="text-error-red text-sm font-mono">&gt; {actionError}</p>
                </div>
              )}

              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setActionError('');
                  }}
                  className="px-6 py-3 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-6 py-3 bg-error-red hover:bg-error-red/90 text-white font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Delete Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function InfoPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center space-x-2 mb-6">
          <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
          <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
            {title}
          </h3>
        </div>
        <div className="space-y-4">{children}</div>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string | null | undefined;
  highlight?: boolean;
}) {
  return (
    <div className="flex justify-between items-center border-b border-panel-border pb-3 last:border-0 last:pb-0">
      <span className="text-text-secondary text-xs uppercase tracking-widest font-mono">
        {label}
      </span>
      <span
        className={`font-mono ${highlight ? 'text-amber font-semibold' : 'text-text-primary'}`}
        style={highlight ? { textShadow: '0 0 6px rgba(255, 149, 0, 0.4)' } : undefined}
      >
        {value || <span className="text-text-tertiary">-</span>}
      </span>
    </div>
  );
}

export default OrderDetailPage;
